import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface EggProductionFormProps {
  stockItemId: string;
  onSuccess: () => void;
}

export const EggProductionForm = ({ stockItemId, onSuccess }: EggProductionFormProps) => {
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState<Date>(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quantity || !date) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    if (!stockItemId) {
      toast.error("Erreur: ID du lot non valide");
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('egg_productions')
        .insert({
          stock_item_id: stockItemId,
          quantity: parseInt(quantity),
          production_date: date.toISOString(),
          notes,
        });

      if (error) throw error;

      toast.success("Production enregistrée avec succès");
      setQuantity("");
      setNotes("");
      setDate(new Date());
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['egg-productions', stockItemId] });
      onSuccess();
    } catch (error: any) {
      console.error("Erreur lors de l'enregistrement:", error);
      toast.error(error.message || "Erreur lors de l'enregistrement de la production");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1">Date de production</label>
        <DatePicker
          selected={date}
          onSelect={(newDate) => newDate && setDate(newDate)}
          placeholder="Sélectionner une date"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Quantité d'œufs</label>
        <Input
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          placeholder="Nombre d'œufs"
          min="0"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Notes (optionnel)</label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ajoutez des notes sur la production"
          rows={3}
        />
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? "Enregistrement..." : "Enregistrer la production"}
      </Button>
    </form>
  );
};